import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { XIcon } from 'lucide-react';
import { apiClient, GlobalTaskResponse, GlobalTaskTenantRunResponse } from '../lib/api';

function statusBadge(status: string | null | undefined) {
  const colors: Record<string, string> = {
    success: 'bg-green-100 text-green-800',
    completed: 'bg-green-100 text-green-800',
    failed: 'bg-red-100 text-red-800',
    partial: 'bg-amber-100 text-amber-800',
    running: 'bg-blue-100 text-blue-800',
    pending: 'bg-gray-100 text-gray-800',
    skipped: 'bg-yellow-100 text-yellow-800',
  };
  const key = status || 'unknown';
  return (
    <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${colors[key] || 'bg-gray-100 text-gray-800'}`}>
      {key}
    </span>
  );
}

export interface GlobalTaskRunFailuresModalProps {
  isOpen: boolean;
  task: GlobalTaskResponse | null;
  onClose: () => void;
}

export default function GlobalTaskRunFailuresModal({
  isOpen,
  task,
  onClose,
}: GlobalTaskRunFailuresModalProps) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tenantRuns, setTenantRuns] = useState<GlobalTaskTenantRunResponse[]>([]);

  useEffect(() => {
    if (!isOpen || !task?.latest_run_id) {
      setTenantRuns([]);
      setError(null);
      return;
    }

    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await apiClient.getGlobalTaskTenantRuns(task.id, task.latest_run_id!);
        if (!cancelled) {
          setTenantRuns(data);
        }
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : 'Failed to load run details');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [isOpen, task?.id, task?.latest_run_id]);

  if (!isOpen || !task) return null;

  const failedRuns = tenantRuns.filter((r) => r.status === 'failed');
  const otherRuns = tenantRuns.filter((r) => r.status !== 'failed');

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center px-4 py-8">
        <div className="fixed inset-0 bg-black/40" onClick={onClose} />
        <div className="relative bg-white rounded-lg shadow-xl max-w-3xl w-full p-6">
          <div className="flex items-start justify-between gap-4 mb-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Latest run details</h3>
              <p className="text-sm text-gray-600 mt-1">
                {task.schedule_name.replace(/_/g, ' ')} · status{' '}
                {statusBadge(task.latest_run_status)}
                {task.latest_run_started_at && (
                  <> · {new Date(task.latest_run_started_at).toLocaleString()}</>
                )}
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
              aria-label="Close"
            >
              <XIcon className="h-5 w-5" />
            </button>
          </div>

          {loading && <p className="text-sm text-gray-500">Loading failed runs…</p>}
          {error && <p className="text-sm text-red-600 mb-3">{error}</p>}

          {!loading && !error && (
            <>
              <h4 className="text-sm font-semibold text-red-700 mb-2">
                Failed tenants ({failedRuns.length})
              </h4>
              {failedRuns.length === 0 ? (
                <p className="text-sm text-gray-500 mb-4">No failed tenant runs in the latest execution.</p>
              ) : (
                <div className="border rounded-md overflow-hidden mb-4 max-h-72 overflow-y-auto">
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr className="text-left text-xs text-gray-500 uppercase">
                        <th className="px-3 py-2">Tenant</th>
                        <th className="px-3 py-2">Status</th>
                        <th className="px-3 py-2">Error</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {failedRuns.map((tr) => (
                        <tr key={tr.id} className="align-top">
                          <td className="px-3 py-2 font-medium text-gray-900">{tr.tenant_name}</td>
                          <td className="px-3 py-2">{statusBadge(tr.status)}</td>
                          <td className="px-3 py-2 text-red-600 whitespace-pre-wrap break-words max-w-md">
                            {tr.error_message || '—'}
                            {tr.result_json && (
                              <details className="mt-1">
                                <summary className="cursor-pointer text-xs text-gray-500">Result details</summary>
                                <pre className="mt-1 text-xs bg-gray-50 border rounded p-2 overflow-auto max-h-40 text-gray-700">
                                  {JSON.stringify(tr.result_json, null, 2)}
                                </pre>
                              </details>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {otherRuns.length > 0 && (
                <details className="mb-4">
                  <summary className="cursor-pointer text-sm text-gray-600">
                    Other tenants in this run ({otherRuns.length})
                  </summary>
                  <ul className="mt-2 text-sm text-gray-700 space-y-1">
                    {otherRuns.map((tr) => (
                      <li key={tr.id} className="flex items-center gap-2">
                        <span>{tr.tenant_name}</span>
                        {statusBadge(tr.status)}
                      </li>
                    ))}
                  </ul>
                </details>
              )}
            </>
          )}

          <div className="flex justify-end gap-2 mt-2">
            <button
              type="button"
              onClick={() => {
                onClose();
                navigate(`/tasks/global/${task.id}`);
              }}
              className="px-4 py-2 text-sm font-medium text-primary-700 bg-white border rounded-md hover:bg-gray-50"
            >
              Open task detail
            </button>
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
